// QUÉ PARTE DEL CONTEXTO DE UN MÓDULO VIAJA EN LA DIRECCIÓN · TC-DR-AT.
//
// «Entrada», «Salida» y «Traspaso» no son módulos: son `escanear` con un
// `tipo`. Antes ese contexto vivía solo en la memoria de React, así que
// recargar en «Entrada» abría el escáner sin modo. Aquí se declara, como
// DATO, qué claves de cada módulo se escriben en la consulta junto a `m`
// y con qué tipo se leen de vuelta. Lo que no está en la tabla viaja solo
// en `history.state` (sobrevive a la recarga, no al enlace copiado).
//
// Es JS puro y sin React: lo usan el módulo de navegación y sus pruebas.
import { contextoDeEtiqueta } from '../accesos/enlaceDeEtiqueta';

export const CLAVE_MODULO = 'm';

// Un valor más largo que esto no es un identificador ni un filtro: no se
// escribe en la dirección (queda en `history.state`).
const LARGO_MAXIMO = 200;

export const CONTEXTO_EN_URL = {
  escanear:    { tipo: 'texto', codigo: 'texto' },
  productos:   { sku: 'texto', buscar: 'texto', proveedor: 'texto' },
  existencias: { sku: 'texto', ubicacion: 'texto' },
  // `imprimir` NO: recargar no debe volver a abrir la hoja de impresión.
  etiquetas:   { sku: 'texto', ubicacion: 'texto' },
  pos:         { folio_pedido: 'texto' },
  compras:     { id_orden: 'numero' },
  conteos:     { id_conteo: 'numero' },
  archivos:    { id_carpeta: 'numero', id_archivo: 'numero' },
  activos:     { buscar: 'texto' },
  // TERMINAL DE PRODUCTO F1 (DISEÑO-F1 D18, contrato v2 §9): el QR de la
  // terminal llega como `/t/<código>` → `?m=terminales&vincular=…` (o
  // `agregar=1`) y el de turno como `?m=terminal-turno&codigo=…&n=…`.
  // Sin estas filas el enlace abre el módulo pero el código no le llega.
  terminales:        { vincular: 'texto', agregar: 'texto' },
  'terminal-turno':  { codigo: 'texto', n: 'texto' },
};

function escribible(valor) {
  if (valor === null || valor === undefined || valor === '') return false;
  if (typeof valor !== 'string' && typeof valor !== 'number') return false;
  return String(valor).length <= LARGO_MAXIMO;
}

/** La dirección de un destino: `<pathname>?m=<id>` más las claves de la tabla. */
export function urlDe(destino, pathname) {
  const m = destino?.m;
  if (!m) return pathname;
  const q = new URLSearchParams();
  q.set(CLAVE_MODULO, m);
  const tabla = CONTEXTO_EN_URL[m];
  const ctx = destino.ctx;
  if (tabla && ctx && typeof ctx === 'object') {
    for (const clave of Object.keys(tabla)) {
      if (escribible(ctx[clave])) q.set(clave, String(ctx[clave]));
    }
  }
  return `${pathname}?${q.toString()}`;
}

/**
 * El contexto que pide la dirección para el módulo `m`, o null si ninguno.
 * La etiqueta leída con la cámara (`?m=escanear&etiqueta=…`) manda: es el
 * enlace profundo que ya existía y sigue dando `{ codigo }`.
 */
export function contextoDeUrl(m, busqueda) {
  const deEtiqueta = contextoDeEtiqueta(m, busqueda);
  if (deEtiqueta) return deEtiqueta;
  const tabla = CONTEXTO_EN_URL[m];
  if (!tabla) return null;
  let q;
  try { q = new URLSearchParams(busqueda ?? ''); } catch { return null; }
  const ctx = {};
  for (const [clave, tipo] of Object.entries(tabla)) {
    if (!q.has(clave)) continue;
    const texto = q.get(clave);
    if (texto === '') continue;
    if (tipo === 'numero') {
      const n = Number(texto);
      if (Number.isFinite(n)) ctx[clave] = n;
    } else {
      ctx[clave] = texto;
    }
  }
  return Object.keys(ctx).length ? ctx : null;
}
